import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { SMSDiscussion, SMSMessage } from '../data/smsDiscussionsData';
import { playSound, triggerHaptic } from '../audio';
import { TTSButton } from './TTSButton';
import { 
  Send, 
  RotateCcw, 
  CheckCheck, 
  Keyboard, 
  Sparkles, 
  ArrowLeft, 
  Award, 
  MessageSquare,
  Volume2
} from 'lucide-react';
import { clearActiveSmsSession, setActiveSmsSession } from '../utils/activeActivityManager';

interface SMSChatViewProps {
  discussion: SMSDiscussion;
  onBack: () => void;
  onCompleted: (discussionId: string, earnedXp: number) => void;
  isAlreadyCompleted?: boolean;
  isAdmin?: boolean;
  playerRole?: 'player' | 'contact';
}

// Unified Tamazight Keyboard Rows
const KEYBOARD_ROW_1 = ['A', 'Z', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'];
const KEYBOARD_ROW_2 = ['Q', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', 'M'];
const KEYBOARD_ROW_3 = ['W', 'X', 'C', 'V', 'B', 'N', "'", '-', ','];
const KEYBOARD_AMAZIGH_ROW = ['Ɛ', 'Ɣ', 'Ḥ', 'Ṣ', 'Ṭ', 'Ḍ', 'Č', 'Ǧ', 'Ž', 'Ṛ', 'Ẓ'];

// Normalization helper matching Crossword character rules
// Accepts standard French Latin letters close to Amazigh characters (e.g. E for Ɛ, G for Ɣ/Ǧ, C for Č, H for Ḥ, D for Ḍ, T for Ṭ, S for Ṣ, R for Ṛ, Z for Ẓ/Ž)
export function normalizeKabyle(text: string): string {
  if (!text) return '';
  return text
    .toLowerCase()
    .replace(/3/g, 'e')
    .replace(/gh/g, 'g')
    .replace(/kh/g, 'x')
    .replace(/č/g, 'c')
    .replace(/ǧ/g, 'g')
    .replace(/ž/g, 'z')
    .replace(/ḍ/g, 'd')
    .replace(/ḥ/g, 'h')
    .replace(/ṛ/g, 'r')
    .replace(/ṣ/g, 's')
    .replace(/ṭ/g, 't')
    .replace(/ẓ/g, 'z')
    .replace(/ɛ/g, 'e')
    .replace(/ɣ/g, 'g')
    .replace(/\blebes\b/g, 'labas')
    .replace(/\blabas\b/g, 'labas')
    .replace(/\binchallah\b/g, 'inshallah')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[.,/#!$%^&*;:{}=\-_`~()?!"'’‘+–—]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export const SMSChatView: React.FC<SMSChatViewProps> = ({
  discussion,
  onBack,
  onCompleted,
  isAlreadyCompleted = false,
  isAdmin = false,
  playerRole = 'player',
}) => {
  // Freeze initial completion state when opening the discussion so modal displays exact awarded XP
  const wasAlreadyCompletedRef = useRef<boolean>(isAlreadyCompleted);
  // Restore saved state
  const savedState = useMemo(() => {
    try {
      const raw = localStorage.getItem('tamazight_active_sms_state');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.discussionId === discussion.id && parsed.playerRole === playerRole) {
          return parsed;
        }
      }
    } catch {}
    return null;
  }, [discussion.id, playerRole]);

  const [earnedXpAwarded, setEarnedXpAwarded] = useState<number>(() => {
    if (savedState) return savedState.earnedXpAwarded;
    return isAlreadyCompleted ? 30 : 150;
  });

  const [nextTargetIndex, setNextTargetIndex] = useState<number>(() => {
    if (savedState) return savedState.nextTargetIndex;
    let index = 0;
    while (index < discussion.messages.length && discussion.messages[index].sender !== playerRole) {
      index++;
    }
    return index;
  });

  const [displayedMessages, setDisplayedMessages] = useState<SMSMessage[]>(() => {
    if (savedState) return savedState.displayedMessages;
    const initial = [];
    let index = 0;
    while (index < discussion.messages.length && discussion.messages[index].sender !== playerRole) {
      initial.push(discussion.messages[index]);
      index++;
    }
    return initial;
  });

  const [inputText, setInputText] = useState<string>(() => savedState ? savedState.inputText : '');
  const [isTypingContact, setIsTypingContact] = useState<boolean>(() => savedState ? savedState.isTypingContact : false);
  const [useVirtualKeyboard, setUseVirtualKeyboard] = useState<boolean>(false);
  const [isCapitalized, setIsCapitalized] = useState<boolean>(false);
  const [illuminatedKey, setIlluminatedKey] = useState<string | null>(null);
  const [isFinished, setIsFinished] = useState<boolean>(() => savedState ? savedState.isFinished : false);
  const [showRewardModal, setShowRewardModal] = useState<boolean>(false);

  // Save state (debounced pour éviter les lags lors de la frappe)
  useEffect(() => {
    const timer = setTimeout(() => {
      localStorage.setItem('tamazight_active_sms_state', JSON.stringify({
        discussionId: discussion.id,
        playerRole,
        earnedXpAwarded,
        nextTargetIndex,
        displayedMessages,
        inputText,
        isTypingContact,
        isFinished
      }));
    }, 300);
    return () => clearTimeout(timer);
  }, [discussion.id, playerRole, earnedXpAwarded, nextTargetIndex, displayedMessages, inputText, isTypingContact, isFinished]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const illuminateTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Find the next target message that the player has to write
  // Player messages are at odd indices in the messages array (1, 3, 5, 7)
  const targetPlayerMessage =
    nextTargetIndex < discussion.messages.length &&
    discussion.messages[nextTargetIndex].sender === playerRole
      ? discussion.messages[nextTargetIndex]
      : null;

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (nextTargetIndex >= discussion.messages.length) {
      if (!isFinished && !targetPlayerMessage) {
        const xp = wasAlreadyCompletedRef.current ? 30 : 150;
        setEarnedXpAwarded(xp);
        setIsFinished(true);
        clearActiveSmsSession(discussion.id);
        setTimeout(() => { playSound('victory'); setShowRewardModal(true); }, 1000);
      }
      return;
    }
    const nextMsg = discussion.messages[nextTargetIndex];
    if (nextMsg.sender !== playerRole) {
      setIsTypingContact(true);
      timeout = setTimeout(() => {
        setIsTypingContact(false);
        setDisplayedMessages(prev => [...prev, nextMsg]);
        setNextTargetIndex(prev => prev + 1);
        playSound('step');
      }, 1100);
    } else {
      setIsTypingContact(false);
    }
    return () => clearTimeout(timeout);
  }, [nextTargetIndex, discussion.messages, playerRole, isFinished, targetPlayerMessage]);

  // Auto-scroll to bottom of chat
  const scrollToBottom = useCallback((smooth = true) => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({
        behavior: smooth ? 'smooth' : 'auto',
        block: 'end',
      });
    }, 100);
  }, []);

  useEffect(() => {
    scrollToBottom(true);
  }, [displayedMessages, isTypingContact, scrollToBottom]);

  // Key feedback animation
  const triggerKeyFeedback = useCallback((key: string) => {
    triggerHaptic('light');
    if (illuminateTimerRef.current) {
      clearTimeout(illuminateTimerRef.current);
    }
    setIlluminatedKey(key);
    illuminateTimerRef.current = setTimeout(() => {
      setIlluminatedKey(null);
    }, 180);
  }, []);

  // Handle virtual key click
  const handleVirtualKey = useCallback(
    (char: string) => {
      playSound('click');
      const letter = isCapitalized ? char.toUpperCase() : char.toLowerCase();
      setInputText((prev) => prev + letter);
    },
    [isCapitalized]
  );

  const handleBackspace = useCallback(() => {
    playSound('click');
    setInputText((prev) => prev.slice(0, -1));
  }, []);

  const handleSpace = useCallback(() => {
    playSound('click');
    setInputText((prev) => prev + ' ');
  }, []);

  // Check how close input is to target
  const isMatch = Boolean(
    targetPlayerMessage &&
      normalizeKabyle(inputText) === normalizeKabyle(targetPlayerMessage.kab)
  );

  // Partial match percentage
  const matchProgress = (() => {
    if (!targetPlayerMessage) return 100;
    const normTarget = normalizeKabyle(targetPlayerMessage.kab);
    const normInput = normalizeKabyle(inputText);
    if (!normTarget) return 0;
    if (normTarget === normInput) return 100;

    let commonPrefix = 0;
    for (let i = 0; i < Math.min(normTarget.length, normInput.length); i++) {
      if (normTarget[i] === normInput[i]) {
        commonPrefix++;
      } else {
        break;
      }
    }
    return Math.min(100, Math.round((commonPrefix / normTarget.length) * 100));
  })();

  // Send message action
  const handleSendMessage = () => {
    if (!targetPlayerMessage) return;
    if (!isMatch) {
      playSound('wrong'); triggerHaptic('heavy'); return;
    }
    playSound('dialogue'); triggerHaptic('medium');
    setDisplayedMessages(prev => [...prev, targetPlayerMessage]);
    setInputText('');
    setNextTargetIndex(prev => prev + 1);
  };

  // Restart discussion
  const handleRestart = () => {
    playSound('click');
    clearActiveSmsSession(discussion.id);
    wasAlreadyCompletedRef.current = true;
    setEarnedXpAwarded(30);
    let index = 0;
    while (index < discussion.messages.length && discussion.messages[index].sender !== playerRole) index++;
    setNextTargetIndex(index);
    setDisplayedMessages(discussion.messages.slice(0, index));
    setInputText('');
    setIsFinished(false);
    setShowRewardModal(false);
    setIsTypingContact(false);
  };


  const playerTurnIndices = discussion.messages
    .map((m, i) => m.sender === playerRole ? i : -1)
    .filter(i => i !== -1);

  const adminJumpToStep = (stepIdx: number) => {
    if (stepIdx >= playerTurnIndices.length) {
      const xp = wasAlreadyCompletedRef.current ? 30 : 150;
      setEarnedXpAwarded(xp);
      setDisplayedMessages([...discussion.messages]);
      setNextTargetIndex(discussion.messages.length);
      setInputText('');
      setIsTypingContact(false);
      setIsFinished(true);
      setShowRewardModal(true);
      playSound('victory');
      onCompleted(discussion.id, xp);
      wasAlreadyCompletedRef.current = true;
    } else {
      const targetIndex = playerTurnIndices[stepIdx];
      const slice = discussion.messages.slice(0, targetIndex);
      setDisplayedMessages(slice);
      setNextTargetIndex(targetIndex);
      setInputText('');
      setIsTypingContact(false);
      setIsFinished(false);
      setShowRewardModal(false);
      playSound(stepIdx === 0 ? 'click' : 'step');
    }
    scrollToBottom(true);
  };

  const adminNextMessage = () => {
    if (isFinished) return;
    const completedPlayerTurns = displayedMessages.filter((m) => m.sender === playerRole).length;
    adminJumpToStep(completedPlayerTurns + 1);
  };

  const adminPrevMessage = () => {
    const totalPlayerTurns = playerTurnIndices.length;
    if (isFinished || showRewardModal) {
      adminJumpToStep(Math.max(0, totalPlayerTurns - 1));
      return;
    }
    const completedPlayerTurns = displayedMessages.filter((m) => m.sender === playerRole).length;
    adminJumpToStep(Math.max(0, completedPlayerTurns - 1));
  };

  const adminAutoFillText = () => {
    if (targetPlayerMessage) {
      setInputText(targetPlayerMessage.kab);
      playSound('click');
    }
  };

  // Total player turns
  const totalPlayerTurns = discussion.messages.filter((m) => m.sender === playerRole).length;
  const completedPlayerTurns = displayedMessages.filter((m) => m.sender === playerRole).length;

  return (
    <div className="fixed inset-0 w-full h-[100dvh] bg-[#1a1c23] text-stone-100 flex flex-col font-sans z-[10005] overflow-hidden select-none">
      {/* Smartphone SMS Header */}
      <header className="py-2.5 px-3 sm:px-4 bg-[#232733] border-b border-stone-700 shadow-md flex items-center justify-between gap-2 shrink-0 z-30">
        <div className="flex items-center gap-2.5 min-w-0">
          <button
            type="button"
            onClick={() => {
              playSound('click');
              onBack();
            }}
            className="p-1.5 bg-stone-800 hover:bg-stone-700 text-stone-200 rounded-lg border border-stone-600 shadow-xs cursor-pointer active:scale-95 transition-all"
            title="Retour à la liste des SMS"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>

          {/* Contact info */}
          <div className="flex items-center gap-2.5 min-w-0">
            <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-lg sm:text-xl border ${discussion.avatarBg} shrink-0 shadow-inner relative`}>
              <span>{discussion.avatar}</span>
              <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 rounded-full border border-[#232733] animate-pulse" />
            </div>

            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <h3 className="font-bold text-xs sm:text-sm text-stone-100 truncate">
                  {discussion.contactName}
                </h3>
              </div>
              <p className="text-[10px] text-emerald-400 font-medium flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                <span>En ligne • Discussion SMS</span>
              </p>
            </div>
          </div>
        </div>

        {/* Progress pill & restart */}
        <div className="flex items-center gap-2 shrink-0">
          <div className="flex flex-col items-end">
            <span className="text-[9px] text-stone-400 font-bold uppercase tracking-wider">
              Progression
            </span>
            <span className="text-xs font-mono font-bold text-amber-400">
              {completedPlayerTurns}/{totalPlayerTurns}
            </span>
          </div>

          <button
            type="button"
            onClick={handleRestart}
            className="p-1.5 bg-stone-800 hover:bg-stone-700 text-stone-300 hover:text-white rounded-lg border border-stone-600 cursor-pointer shadow-xs transition-all"
            title="Recommencer la discussion"
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        </div>
      </header>

      {/* Chat Messages Screen */}
      <div 
        className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-4 space-y-3 bg-[radial-gradient(#2d3345_1px,transparent_1px)] [background-size:16px_16px] bg-[#171922]"
      >
        {/* Context badge at top */}
        <div className="flex justify-center">
          <div className="bg-[#232733]/90 backdrop-blur-xs border border-stone-700/80 px-3 py-1.5 rounded-full text-[11px] text-stone-300 text-center max-w-md shadow-xs">
            💬 {discussion.contextDesc}
          </div>
        </div>

        {/* Messages list */}
        {displayedMessages.map((msg, idx) => {
          const isContact = msg.sender === 'contact';
          return (
            <motion.div
              key={msg.id || idx}
              initial={{ opacity: 0, y: 10, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.2 }}
              className={`flex flex-col ${isContact ? 'items-start' : 'items-end'} w-full`}
            >
              <div
                className={`max-w-[88%] sm:max-w-[75%] p-3 rounded-2xl shadow-md border ${
                  isContact
                    ? 'bg-[#282d3d] border-stone-600 text-stone-100 rounded-tl-xs'
                    : 'bg-gradient-to-br from-amber-700 to-amber-900 border-amber-600 text-white rounded-tr-xs'
                }`}
              >
                {/* Kabyle text + TTS */}
                <div className="flex items-start justify-between gap-2">
                  <p className="font-bold text-sm sm:text-base leading-snug tracking-wide text-white">
                    {msg.kab}
                  </p>
                  <TTSButton
                    text={msg.kab}
                    size="sm"
                    className={`${
                      isContact
                        ? 'bg-stone-700/80 hover:bg-stone-600 text-amber-300 border-stone-600'
                        : 'bg-amber-800/80 hover:bg-amber-700 text-white border-amber-600'
                    } shrink-0`}
                  />
                </div>

                {/* French Translation */}
                <div className={`mt-1.5 pt-1.5 border-t text-xs sm:text-sm font-sans ${
                  isContact
                    ? 'border-stone-600/70 text-amber-200/90 italic'
                    : 'border-amber-600/60 text-amber-100 italic'
                }`}>
                  <span className="text-[10px] font-mono not-italic uppercase tracking-wider opacity-60 mr-1.5">
                    FR:
                  </span>
                  {msg.fr}
                </div>

                {/* Timestamp + status */}
                <div className="mt-1 flex items-center justify-end gap-1 text-[9px] text-stone-400 font-mono">
                  <span>{msg.time}</span>
                  {!isContact && <CheckCheck className="w-3 h-3 text-emerald-400 inline" />}
                </div>
              </div>
            </motion.div>
          );
        })}

        {/* Contact typing animation */}
        {isTypingContact && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-1.5 bg-[#282d3d] border border-stone-600 px-3.5 py-2.5 rounded-2xl rounded-tl-xs w-20 shadow-md"
          >
            <motion.span
              animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1.2, 0.8] }}
              transition={{ repeat: Infinity, duration: 1, delay: 0 }}
              className="w-1.5 h-1.5 bg-amber-400 rounded-full"
            />
            <motion.span
              animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1.2, 0.8] }}
              transition={{ repeat: Infinity, duration: 1, delay: 0.2 }}
              className="w-1.5 h-1.5 bg-amber-400 rounded-full"
            />
            <motion.span
              animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1.2, 0.8] }}
              transition={{ repeat: Infinity, duration: 1, delay: 0.4 }}
              className="w-1.5 h-1.5 bg-amber-400 rounded-full"
            />
          </motion.div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Target Response Banner & Input Section (Permanently rendered to avoid jarring layout shifts) */}
      {!isFinished && (
        <div className="bg-[#1e222d] border-t border-stone-700 p-2.5 sm:p-3 flex flex-col gap-2 shrink-0 z-20">
          {/* Target message to copy guidance (With smooth exit/enter animations and stable container) */}
          <div className="bg-[#2b3040] border border-amber-500/40 rounded-xl p-2.5 sm:p-3 shadow-sm min-h-[96px] flex flex-col justify-center overflow-hidden">
            <AnimatePresence mode="wait">
              {targetPlayerMessage && !isTypingContact ? (
                <motion.div
                  key={targetPlayerMessage.id || `target-${nextTargetIndex}`}
                  initial={{ opacity: 0, y: 8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -12, scale: 0.96 }}
                  transition={{ duration: 0.22 }}
                  className="flex flex-col gap-1 w-full"
                >
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <div className="flex items-center gap-1.5 text-amber-400 font-bold text-xs uppercase tracking-wide">
                      <span>✍️</span>
                      <span>Recopiez la réponse en kabyle :</span>
                    </div>
                    <TTSButton
                      text={targetPlayerMessage.kab}
                      size="sm"
                      className="bg-stone-800 hover:bg-stone-700 text-amber-300 border-stone-600 shrink-0"
                    />
                  </div>

                  {/* Target Kabyle text */}
                  <p className="font-bold text-sm sm:text-base text-white select-all font-mono leading-snug">
                    {targetPlayerMessage.kab}
                  </p>

                  {/* French translation */}
                  <p className="text-xs text-amber-200/80 italic font-sans">
                    « {targetPlayerMessage.fr} »
                  </p>

                  {/* Progress bar matching */}
                  <div className="mt-1.5 flex items-center gap-2">
                    <div className="flex-1 bg-stone-800 rounded-full h-1.5 overflow-hidden border border-stone-600">
                      <div
                        className={`h-full rounded-full transition-all duration-300 ${
                          isMatch ? 'bg-emerald-500' : 'bg-amber-500'
                        }`}
                        style={{ width: `${matchProgress}%` }}
                      />
                    </div>
                    <span className={`text-[10px] font-mono font-bold ${isMatch ? 'text-emerald-400' : 'text-stone-400'}`}>
                      {isMatch ? '✓ Parfait' : `${matchProgress}%`}
                    </span>
                  </div>
                </motion.div>
              ) : isTypingContact ? (
                <motion.div
                  key="typing-indicator-box"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                  className="flex items-center justify-between py-1 px-1 w-full"
                >
                  <div className="flex items-center gap-2.5">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm border ${discussion.avatarBg} shrink-0`}>
                      <span>{discussion.avatar}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-xs font-bold text-amber-300">
                        {discussion.contactName} est en train d'écrire...
                      </span>
                      <span className="text-[10px] text-stone-400 italic">
                        Message envoyé • En attente de la réponse
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 bg-stone-800/90 px-3 py-1.5 rounded-full border border-stone-600/80 shadow-inner">
                    <motion.span
                      animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1.2, 0.8] }}
                      transition={{ repeat: Infinity, duration: 1, delay: 0 }}
                      className="w-1.5 h-1.5 bg-amber-400 rounded-full"
                    />
                    <motion.span
                      animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1.2, 0.8] }}
                      transition={{ repeat: Infinity, duration: 1, delay: 0.2 }}
                      className="w-1.5 h-1.5 bg-amber-400 rounded-full"
                    />
                    <motion.span
                      animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1.2, 0.8] }}
                      transition={{ repeat: Infinity, duration: 1, delay: 0.4 }}
                      className="w-1.5 h-1.5 bg-amber-400 rounded-full"
                    />
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="done-indicator-box"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex items-center justify-center py-2 text-xs font-bold text-emerald-400 gap-2"
                >
                  <span>✓</span>
                  <span>Conversation terminée avec succès !</span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Input field + Send button */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSendMessage();
            }}
            className="flex items-center gap-2"
          >
            <div className="relative flex-1">
              <input
                ref={inputRef}
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                disabled={isTypingContact || !targetPlayerMessage}
                placeholder={
                  isTypingContact
                    ? `${discussion.contactName} est en train d'écrire...`
                    : !targetPlayerMessage
                    ? 'En attente...'
                    : 'Tapez le message en kabyle ici...'
                }
                inputMode={useVirtualKeyboard ? 'none' : 'text'}
                autoComplete="off"
                autoCorrect="off"
                spellCheck="false"
                style={{ fontSize: '16px', touchAction: 'manipulation' }}
                className={`w-full py-2 px-3 sm:px-3.5 pr-8 bg-[#15171e] text-white text-[16px] font-mono rounded-xl border-2 transition-all outline-none ${
                  isTypingContact
                    ? 'border-stone-700 bg-stone-900/60 opacity-70 cursor-not-allowed'
                    : isMatch
                    ? 'border-emerald-500 ring-2 ring-emerald-500/30'
                    : 'border-stone-600 focus:border-amber-500'
                }`}
              />
              {inputText.length > 0 && !isTypingContact && (
                <button
                  type="button"
                  onClick={() => setInputText('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-200 text-xs font-bold"
                >
                  ✕
                </button>
              )}
            </div>

            <button
              type="submit"
              disabled={!isMatch || isTypingContact}
              className={`py-2 px-4 rounded-xl font-bold text-xs uppercase tracking-wider flex items-center gap-1.5 transition-all shadow-md shrink-0 cursor-pointer ${
                isMatch && !isTypingContact
                  ? 'bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-500 hover:to-emerald-600 text-white active:scale-95 shadow-emerald-900/40'
                  : 'bg-stone-800 text-stone-500 border border-stone-700 cursor-not-allowed opacity-60'
              }`}
            >
              <span>Envoyer</span>
              <Send className="w-3.5 h-3.5" />
            </button>
          </form>

          {/* Virtual Keyboard Toggle header */}
          <div className="flex items-center justify-between pt-1 border-t border-stone-700/60 text-[10px]">
            <div className="flex items-center gap-1.5 text-stone-300 font-bold uppercase tracking-wider">
              <Keyboard className="w-3 h-3 text-amber-400" />
              <span>Clavier Berbère</span>
            </div>

            <div
              className="flex items-center gap-2 cursor-pointer group"
              onClick={() => setUseVirtualKeyboard(!useVirtualKeyboard)}
              title="Activer/Désactiver le clavier virtuel berbère"
            >
              <span
                className={`text-[9px] font-bold transition-colors uppercase tracking-wider ${
                  useVirtualKeyboard ? 'text-amber-400 font-black' : 'text-stone-400'
                }`}
              >
                {useVirtualKeyboard ? 'Virtuel' : 'Système'}
              </span>
              <button
                type="button"
                className={`relative inline-flex h-4 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full border border-transparent transition-colors focus:outline-none ${
                  useVirtualKeyboard ? 'bg-amber-600' : 'bg-stone-600'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow-md transition duration-200 ease-in-out ${
                    useVirtualKeyboard ? 'translate-x-2' : '-translate-x-2'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Virtual Keyboard Keys */}
          {useVirtualKeyboard && (
            <div className={`flex flex-col gap-1 sm:gap-1.5 pt-1 transition-opacity ${isTypingContact ? 'opacity-60 pointer-events-none' : 'opacity-100'}`}>
              {/* Row 1: A Z E R T Y U I O P */}
              <div className="flex justify-center gap-1 sm:gap-1.5 w-full">
                {KEYBOARD_ROW_1.map((k) => (
                  <button
                    key={k}
                    type="button"
                    onPointerDown={() => triggerKeyFeedback(k)}
                    onClick={() => handleVirtualKey(k)}
                    className={`flex-1 h-8 sm:h-9 bg-[#2b3040] hover:bg-[#363c50] text-stone-100 font-mono font-bold text-xs sm:text-sm rounded-lg border border-stone-600 hover:border-amber-400 shadow-2xs flex items-center justify-center cursor-pointer min-w-0 active:bg-amber-700 ${
                      illuminatedKey === k ? 'bg-amber-600 border-amber-300' : ''
                    }`}
                  >
                    <span>{isCapitalized ? k : k.toLowerCase()}</span>
                  </button>
                ))}
              </div>

              {/* Row 2: Q S D F G H J K L M */}
              <div className="flex justify-center gap-1 sm:gap-1.5 w-full">
                {KEYBOARD_ROW_2.map((k) => (
                  <button
                    key={k}
                    type="button"
                    onPointerDown={() => triggerKeyFeedback(k)}
                    onClick={() => handleVirtualKey(k)}
                    className={`flex-1 h-8 sm:h-9 bg-[#2b3040] hover:bg-[#363c50] text-stone-100 font-mono font-bold text-xs sm:text-sm rounded-lg border border-stone-600 hover:border-amber-400 shadow-2xs flex items-center justify-center cursor-pointer min-w-0 active:bg-amber-700 ${
                      illuminatedKey === k ? 'bg-amber-600 border-amber-300' : ''
                    }`}
                  >
                    <span>{isCapitalized ? k : k.toLowerCase()}</span>
                  </button>
                ))}
              </div>

              {/* Row 3: W X C V B N ' - , */}
              <div className="flex justify-center gap-1 sm:gap-1.5 w-full">
                {KEYBOARD_ROW_3.map((k) => (
                  <button
                    key={k}
                    type="button"
                    onPointerDown={() => triggerKeyFeedback(k)}
                    onClick={() => handleVirtualKey(k)}
                    className={`flex-1 h-8 sm:h-9 bg-[#2b3040] hover:bg-[#363c50] text-stone-100 font-mono font-bold text-xs sm:text-sm rounded-lg border border-stone-600 hover:border-amber-400 shadow-2xs flex items-center justify-center cursor-pointer min-w-0 active:bg-amber-700 ${
                      illuminatedKey === k ? 'bg-amber-600 border-amber-300' : ''
                    }`}
                  >
                    <span>{isCapitalized ? k : k.toLowerCase()}</span>
                  </button>
                ))}
              </div>

              {/* Row 4: Dedicated Berber Characters Ribbon */}
              <div className="flex justify-center gap-0.5 sm:gap-1 w-full bg-amber-950/40 p-1 rounded-xl border border-amber-600/40">
                {KEYBOARD_AMAZIGH_ROW.map((k) => (
                  <button
                    key={k}
                    type="button"
                    onPointerDown={() => triggerKeyFeedback(k)}
                    onClick={() => handleVirtualKey(k)}
                    className={`flex-1 h-7.5 sm:h-8.5 bg-amber-900/70 hover:bg-amber-800 text-amber-200 font-mono font-black text-xs sm:text-sm rounded-lg border border-amber-500/60 flex items-center justify-center cursor-pointer min-w-0 shadow-xs active:bg-amber-600 ${
                      illuminatedKey === k ? 'bg-amber-500 text-white' : ''
                    }`}
                  >
                    <span>{isCapitalized ? k : k.toLowerCase()}</span>
                  </button>
                ))}
              </div>

              {/* Row 5: Action Controls (MAJ + ESPACE + EFFACER) */}
              <div className="flex justify-center gap-1 sm:gap-1.5 w-full pt-0.5">
                <button
                  type="button"
                  onPointerDown={() => triggerKeyFeedback('shift')}
                  onClick={() => setIsCapitalized(!isCapitalized)}
                  className={`flex-[1.5] h-8 sm:h-9 text-xs font-bold rounded-lg border flex items-center justify-center gap-1 cursor-pointer transition-all shadow-2xs ${
                    isCapitalized
                      ? 'bg-amber-600 text-white border-amber-400 font-black'
                      : 'bg-[#2b3040] text-stone-300 border-stone-600 hover:border-amber-400'
                  }`}
                >
                  <span>⇧</span>
                  <span className="hidden sm:inline">MAJ</span>
                </button>

                <button
                  type="button"
                  onPointerDown={() => triggerKeyFeedback('space')}
                  onClick={handleSpace}
                  className="flex-[4] h-8 sm:h-9 bg-[#2b3040] hover:bg-[#363c50] text-stone-300 font-mono text-xs rounded-lg border border-stone-600 hover:border-amber-400 flex items-center justify-center cursor-pointer uppercase tracking-widest shadow-2xs active:bg-amber-700"
                >
                  Espace
                </button>

                <button
                  type="button"
                  onPointerDown={() => triggerKeyFeedback('backspace')}
                  onClick={handleBackspace}
                  className="flex-[1.5] h-8 sm:h-9 bg-[#2b3040] hover:bg-rose-900/60 text-stone-200 hover:text-rose-200 font-bold text-xs rounded-lg border border-stone-600 hover:border-rose-500 flex items-center justify-center cursor-pointer shadow-2xs active:bg-rose-700"
                  title="Effacer"
                >
                  <span>⌫</span>
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Discussion Completed Overlay */}
      <AnimatePresence>
        {showRewardModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm select-none"
          >
            <motion.div
              initial={{ scale: 0.85, y: 15 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: -10 }}
              transition={{ type: 'spring', stiffness: 350, damping: 25 }}
              className="bg-[#232733] border-2 border-amber-500 rounded-2xl p-5 sm:p-6 max-w-sm w-full text-center shadow-2xl flex flex-col items-center gap-3.5 relative"
            >
              {/* Close button to review discussion */}
              <button
                type="button"
                onClick={() => {
                  playSound('click');
                  setShowRewardModal(false);
                }}
                className="absolute top-3 right-3 text-stone-400 hover:text-white transition-colors cursor-pointer"
                title="Fermer pour relire"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Animated Trophy Icon */}
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center text-3xl shadow-lg border border-amber-300/60 animate-bounce">
                🎉
              </div>

              <div>
                <h3 className="text-lg sm:text-xl font-black text-amber-300 uppercase tracking-wide">
                  Discussion Complétée !
                </h3>
                <p className="text-xs text-stone-300 mt-1">
                  Vous avez maîtrisé tous les échanges de la conversation avec{' '}
                  <strong className="text-white">{discussion.contactName}</strong>.
                </p>
              </div>

              {/* XP Pill */}
              <div className="flex items-center gap-2 bg-amber-950/80 border border-amber-500 px-4 py-2 rounded-xl">
                <Sparkles className="w-4 h-4 text-amber-400" />
                <span className="text-sm font-black text-amber-300 font-mono">
                  +{earnedXpAwarded} XP Reçu
                </span>
              </div>

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row gap-2 w-full pt-1">
                <button
                  type="button"
                  onClick={handleRestart}
                  className="flex-1 py-2 px-3 bg-stone-700 hover:bg-stone-600 text-stone-200 font-bold text-xs uppercase rounded-xl border border-stone-500 cursor-pointer transition-all"
                >
                  Refaire l'échange
                </button>
                <button
                  type="button"
                  onClick={() => {
                    playSound('click');
                    localStorage.removeItem('tamazight_active_sms_session');
                    onBack();
                  }}
                  className="flex-1 py-2 px-3 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-md cursor-pointer transition-all"
                >
                  Retour aux SMS
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ========================================================= */}
      {/* BARRE D'ADMIN SMS / TRICHE (NAVIGATION FLÈCHES & TOURS)   */}
      {/* ========================================================= */}
      {isAdmin && (
        <div className="w-full bg-slate-950/95 border-t border-amber-500/70 px-2 py-1 pb-[max(env(safe-area-inset-bottom),6px)] flex items-center justify-between sm:justify-center gap-1 sm:gap-2 shrink-0 z-40 text-amber-300 shadow-[0_-2px_12px_rgba(0,0,0,0.6)] backdrop-blur-sm select-none min-h-[40px]">
          {/* Arrow Step Navigation */}
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={adminPrevMessage}
              disabled={displayedMessages.length <= 1}
              className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[11px] sm:text-xs font-black transition-all cursor-pointer ${
                displayedMessages.length <= 1
                  ? 'text-slate-600 opacity-40 cursor-not-allowed'
                  : 'text-amber-300 bg-amber-950/80 hover:bg-amber-900 border border-amber-500/70 active:scale-90 shadow-sm'
              }`}
              title="Message précédent"
            >
              <svg className="w-2.5 h-2.5 fill-current inline-block shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                <polygon points="17,4 7,12 17,20" />
              </svg>
            </button>

            <span className="text-[10px] sm:text-xs text-amber-300 font-black uppercase tracking-wider whitespace-nowrap px-0.5">
              <span className="hidden sm:inline">✨ Msg </span>
              <span className="sm:hidden">✨ </span>
              {displayedMessages.length}/{discussion.messages.length}
            </span>

            <button
              type="button"
              onClick={adminNextMessage}
              disabled={isFinished}
              className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[11px] sm:text-xs font-black transition-all cursor-pointer ${
                isFinished
                  ? 'text-slate-600 opacity-40 cursor-not-allowed'
                  : 'text-amber-300 bg-amber-950/80 hover:bg-amber-900 border border-amber-500/70 active:scale-90 shadow-sm'
              }`}
              title="Message suivant"
            >
              <svg className="w-2.5 h-2.5 fill-current inline-block shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                <polygon points="7,4 17,12 7,20" />
              </svg>
            </button>
          </div>

          {/* Direct Turn Switcher (T1, T2, T3... 🏁) */}
          <div className="flex items-center gap-0.5 sm:gap-1 border-l border-amber-500/40 pl-1.5 sm:pl-2 shrink-0">
            {Array.from({ length: totalPlayerTurns + 1 }).map((_, stepIdx) => {
              const isCurrent = stepIdx === completedPlayerTurns && !isFinished;
              const isAllDone = stepIdx === totalPlayerTurns && isFinished;
              const active = isCurrent || isAllDone;
              return (
                <button
                  key={stepIdx}
                  type="button"
                  onClick={() => adminJumpToStep(stepIdx)}
                  className={`px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-bold font-mono transition-all cursor-pointer ${
                    active
                      ? 'bg-amber-400 text-slate-950 font-black ring-1 ring-amber-200 shadow-xs'
                      : 'text-amber-300/80 bg-slate-900 hover:bg-amber-950 border border-amber-500/30'
                  }`}
                  title={stepIdx === totalPlayerTurns ? 'Terminer la discussion' : `Aller au tour ${stepIdx + 1}`}
                >
                  {stepIdx === totalPlayerTurns ? '🏁' : `T${stepIdx + 1}`}
                </button>
              );
            })}
          </div>

          {/* Quick Auto-Fill Text Helper */}
          {targetPlayerMessage && !isFinished && (
            <button
              type="button"
              onClick={adminAutoFillText}
              className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-950/90 border border-emerald-500/80 text-emerald-300 hover:bg-emerald-900 cursor-pointer ml-1 active:scale-95 transition-all shadow-xs"
              title="Copier le texte kabyle cible dans le champ de saisie"
            >
              <span>✍️ Remplir</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};
